// FailedPaymentsPage — lists failed Stripe invoices with optional status filter.
// Data from GET /api/v1/failed-invoices (tenant-scoped, RLS-enforced).

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";
import { fetchFailedInvoices, type FailedInvoice } from "../api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@avagency/frontend/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@avagency/frontend/components/ui/table";
import { Badge } from "@avagency/frontend/components/ui/badge";
import { EmptyState } from "@avagency/frontend/components/shell/EmptyState";
import { ErrorState } from "@avagency/frontend/components/shell/ErrorState";
import { LoadingState } from "@avagency/frontend/components/shell/LoadingState";

type StatusFilter =
  | "all"
  | "open"
  | "recovered"
  | "exhausted"
  | "cancelled"
  | "free_tier_observed";

const STATUS_FILTERS: { label: string; value: StatusFilter }[] = [
  { label: "All", value: "all" },
  { label: "Open", value: "open" },
  { label: "Recovered", value: "recovered" },
  { label: "Exhausted", value: "exhausted" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Free tier", value: "free_tier_observed" },
];

type BadgeVariant = "default" | "secondary" | "destructive" | "outline";

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  open: "default",
  recovered: "secondary",
  exhausted: "destructive",
  cancelled: "outline",
  free_tier_observed: "outline",
};

const STATUS_CLASS: Record<string, string> = {
  open:               "dundial-status-open",
  recovered:          "dundial-status-recovered",
  exhausted:          "dundial-status-exhausted",
  cancelled:          "dundial-status-cancelled",
  free_tier_observed: "dundial-status-free-tier",
};

function statusVariant(status: string): BadgeVariant {
  return STATUS_VARIANT[status] ?? "outline";
}

function statusClass(status: string): string {
  return STATUS_CLASS[status] ?? "";
}

function formatCents(cents: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function FailedPaymentsPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const q = useQuery<FailedInvoice[]>({
    queryKey: ["dundial", "failed-invoices", statusFilter],
    queryFn: () =>
      fetchFailedInvoices({
        limit: 100,
        status: statusFilter === "all" ? undefined : statusFilter,
      }),
  });

  const filterLabel =
    STATUS_FILTERS.find((f) => f.value === statusFilter)?.label ?? statusFilter;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Failed Payments</h1>
        <p className="mt-1 font-mono text-xs uppercase tracking-widest text-muted-foreground">
          Stripe invoices monitored for retry
        </p>
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by status">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            aria-pressed={statusFilter === f.value}
            className={[
              "rounded-full border px-3 py-1 font-mono text-[11px] uppercase tracking-wider transition-all duration-150",
              statusFilter === f.value
                ? "dundial-pill-active"
                : "border-border bg-muted/40 text-muted-foreground hover:border-border/80 hover:bg-muted/60 hover:text-foreground",
            ].join(" ")}
          >
            {f.label}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Failed invoices</CardTitle>
          <CardDescription>
            Most recent 100 failed payments
            {statusFilter !== "all" ? ` with status "${filterLabel}"` : ""}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {q.isLoading && <LoadingState rows={5} />}
          {q.error && <ErrorState error={q.error} retry={() => q.refetch()} />}
          {q.data && q.data.length === 0 && (
            <EmptyState
              icon={
                <div className="dundial-empty-glyph" aria-hidden>
                  <AlertCircle className="h-6 w-6 dundial-icon-warn" />
                </div>
              }
              title="No failed payments"
              description={
                statusFilter === "all"
                  ? "No failed payments detected yet. DunDial will catch them as they come in."
                  : `No payments with status "${filterLabel}".`
              }
            />
          )}
          {q.data && q.data.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>First failed</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Decline code</TableHead>
                  <TableHead>Attempts</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Next retry</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {q.data.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell>{formatDate(inv.first_failed_at)}</TableCell>
                    <TableCell className="font-medium">
                      {formatCents(inv.amount_cents, inv.currency)}
                    </TableCell>
                    <TableCell>
                      {inv.decline_code
                        ? <span className="dundial-decline-chip">{inv.decline_code}</span>
                        : <span className="text-muted-foreground">—</span>
                      }
                    </TableCell>
                    <TableCell className="tabular-nums">{inv.attempts_made}</TableCell>
                    <TableCell>
                      <Badge
                        variant={statusVariant(inv.status)}
                        className={statusClass(inv.status)}
                      >
                        {inv.status.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {inv.next_retry_at ? formatDate(inv.next_retry_at) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
